import { Component, LOCALE_ID, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  LucideAngularModule,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  CheckIcon,
  DollarSign,
  AlertCircle,
  ChevronLeft,
} from 'lucide-angular';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { ModalService } from '../../../../core/services/modal.service';
import { ModalDataService } from '../../../../core/services/modal-data.service';
import { SafePipe } from '../../../pipes/safe.pipe';
import {
  Agenda,
  AppointmentService,
} from '../../../../core/services/appointment.service';
import { SwalService } from '../../../../core/services/swal.service';
import { ReferentielService } from '../../../../core/services/referentiel.service';
import { UserService } from '../../../../core/services/user.service';
import { User } from '../../../../core/interfaces/user.interface';
import { Country } from '../../../../core/interfaces/doctor.interface';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';

// the second parameter 'fr' is optional
registerLocaleData(localeFr, 'fr');
interface Timeslot {
  date: string;
  agendaId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  startDateFull: string;
  endDateFull: string;
  isAvailable: boolean;
  acomptePrice?: number;
}

interface AppointmentReason {
  _id: string;
  createdBy: string;
  creationDate: string;
  updatedBy: string | null;
  updatedDate: string | null;
  disable: boolean;
  documentGenerationRequired: boolean;
  label: string;
  id: number;
  eyoneExternalId: string | null;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

@Component({
  selector: 'app-appointment-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, SafePipe],
  templateUrl: './appointment-modal.component.html',
  providers: [{ provide: LOCALE_ID, useValue: 'fr' }],
  styles: [],
})
export class AppointmentModalComponent implements OnInit {
  readonly CalendarIcon = CalendarIcon;
  readonly ClockIcon = ClockIcon;
  readonly UserIcon = UserIcon;
  readonly CheckIcon = CheckIcon;
  readonly DollarSign = DollarSign;
  readonly AlertCircleIcon = AlertCircle;
  readonly ChevronLeftIcon = ChevronLeft;

  agenda?: Agenda;

  selectedCountry: Country | null = null;

  slot?: Timeslot;
  doctorId?: string;
  structureId?: string;
  doctorName?: string;
  structureName?: string;
  specialisations: any[] = [];
  appointmentForm!: FormGroup;
  isLoading = false;
  appointmentReasons: any[] = [];

  // Propriétés pour la gestion des sous-comptes
  subAccounts: any[] = [];
  currentUser: any = null;
  selectedPatientId: string = '';

  /** Étape du modal : 1 = formulaire, 2 = paiement acompte, 3 = iframe checkout */
  currentStep: 1 | 2 | 3 = 1;
  /** URL du checkout à afficher en iframe (après création RDV avec paiement) */
  checkoutLinkUrl: string | null = null;

  constructor(
    private fb: FormBuilder,
    private modalService: ModalService,
    private appointmentService: AppointmentService,
    private swalService: SwalService,
    private referentielService: ReferentielService,
    private userService: UserService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem('defaultCountry');
      if (saved) {
        try {
          this.selectedCountry = JSON.parse(saved);
        } catch {
          this.selectedCountry = null;
        }
      }
    }
    // Récupérer les données depuis ModalDataService
    const modalData = ModalDataService.getData();
    console.log('AppointmentModalComponent: Données du modal:', modalData);

    if (modalData) {
      this.slot = modalData.slot;
      this.doctorId = modalData.doctorId;
      this.structureId = modalData.structureId;
      this.doctorName = modalData.doctorName;
      this.structureName = modalData.structureName;
      this.specialisations = modalData.specialisations || [];
      // Récupérer les données des sous-comptes
      this.subAccounts = modalData.subAccounts || [];
      this.appointmentReasons = modalData.doctorReasonAppts || [];
      this.agenda = modalData.agenda;
      console.log('AppointmentModalComponent: Agenda:', this.agenda);
    }

    // Récupérer l'utilisateur courant depuis les données du modal
    if (modalData && modalData.currentUser) {
      this.currentUser = modalData.currentUser;
      console.log(
        'AppointmentModalComponent: Utilisateur courant depuis les données du modal:',
        this.currentUser,
      );
    }

    this.initForm();
    this.appointmentForm
      .get('specialisation')
      ?.setValue(this.specialisations[0] || '');

    // Exiger l'acceptation des conditions d'annulation quand des règles existent
    if (this.getCancellationRules().length > 0) {
      this.appointmentForm
        .get('acceptCancellationTerms')
        ?.setValidators(Validators.requiredTrue);
    }

    this.loadSubAccountsIfNeeded();

    // slot
    console.log('Slot:', this.slot);
    console.log(
      'AppointmentModalComponent: Sous-comptes disponibles:',
      this.subAccounts,
    );
  }

  private initForm(): void {
    this.appointmentForm = this.fb.group({
      patientId: ['', Validators.required],
      reason: ['', [Validators.required]],
      specialisation: ['', Validators.required],
      notes: [''],
      acceptCancellationTerms: [false, []],
    });
  }

  private loadAppointmentReasons(): void {
    this.referentielService.getAppointmentReasons().subscribe({
      next: (reasons) => {
        this.appointmentReasons = reasons;
        console.log('Appointment reasons loaded:', reasons);
      },
      error: (error) => {
        console.error('Error loading appointment reasons:', error);
        this.swalService.error(
          'Erreur',
          'Impossible de charger les motifs de rendez-vous',
        );
      },
    });
  }

  private loadSubAccountsIfNeeded(): void {
    // Si les sous-comptes ne sont pas disponibles, les récupérer depuis l'API
    if (!this.subAccounts || this.subAccounts.length === 0) {
      console.log(
        "AppointmentModalComponent: Aucun sous-compte disponible, récupération depuis l'API...",
      );

      this.userService.getSubAccounts().subscribe({
        next: (response: any) => {
          console.log(
            'AppointmentModalComponent: Réponse API sous-comptes:',
            response,
          );
          const subAccounts =
            response.subAccounts || response.data || response || [];
          console.log(
            'AppointmentModalComponent: Sous-comptes récupérés:',
            subAccounts,
          );

          this.subAccounts = subAccounts;
        },
        error: (error: any) => {
          console.error(
            'AppointmentModalComponent: Erreur lors de la récupération des sous-comptes:',
            error,
          );
          this.swalService.error(
            'Erreur',
            'Impossible de charger les informations des patients',
          );
        },
      });
    } else {
      console.log(
        'AppointmentModalComponent: Sous-comptes déjà disponibles:',
        this.subAccounts,
      );
    }
  }

  formatDate(dateString?: string): string {
    if (!dateString) return '';

    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    return date.toLocaleDateString('fr-FR', options);
  }

  /**
   * Soumission étape 1 : si acompte requis, passer à l'étape 2 ; sinon créer le rendez-vous.
   */
  onSubmit(): void {
    if (!this.appointmentForm.valid || !this.slot || !this.selectedPatientId) {
      if (!this.selectedPatientId) {
        this.swalService.error('Erreur', 'Veuillez sélectionner un patient');
      }
      return;
    }

    if (this.currentStep === 1 && this.requiresDeposit()) {
      this.currentStep = 2;
      return;
    }

    this.doCreateAppointment();
  }

  goBackToStep1(): void {
    this.currentStep = 1;
  }

  /** Fermer le modal après paiement (étape 3) et rediriger vers les rendez-vous. */
  closeAfterPayment(): void {
    this.closeModal();
    this.router.navigate([
      `/${this.selectedCountry?.code}/passport/health-tracking`,
    ]);
  }

  /** Confirmer et payer (étape 2) : envoi de la réservation avec acompte. */
  confirmAndPay(): void {
    this.doCreateAppointment();
  }

  private doCreateAppointment(): void {
    if (!this.slot || !this.selectedPatientId) return;

    const selectedReason = this.appointmentReasons.find(
      (reason) => reason.label === this.appointmentForm.value.reason,
    );
    if (!selectedReason) {
      this.swalService.error('Erreur', 'Veuillez sélectionner un motif valide');
      return;
    }

    let selectedPatient = this.subAccounts.find(
      (patient) => patient.passportNumber === this.selectedPatientId,
    );
    if (
      !selectedPatient &&
      this.currentUser &&
      this.currentUser.passportNumber === this.selectedPatientId
    ) {
      selectedPatient = this.currentUser;
    }
    if (!selectedPatient) {
      this.swalService.error(
        'Erreur',
        'Veuillez sélectionner un patient valide',
      );
      return;
    }

    const appointmentData = {
      passportNumber: selectedPatient.passportNumber,
      reason: {
        id: String(selectedReason._id || selectedReason.reasonApptId),
        label: selectedReason.label,
      },
      externalId: this.doctorId || '',
      structure: {
        id: this.structureId || '',
        name: this.structureName || 'Hospital PSM',
      },
      date: this.slot?.date || '',
      startTime: this.slot?.startTime || '',
      endTime: this.slot?.endTime || '',
      notes: this.appointmentForm.value.notes || '',
      specialisation: this.appointmentForm.value.specialisation || '',
      acomptePrice: this.slot?.acomptePrice || 0,
    };

    this.isLoading = true;
    this.appointmentService.createAppointment(appointmentData).subscribe({
      next: (response) => {
        this.isLoading = false;
        const payment = response?.payment;
        if (payment?.checkoutLink) {
          this.checkoutLinkUrl = payment.checkoutLink.replace(
            'http:',
            'https:',
          );
          this.currentStep = 3;
          return;
        }
        if (response?.status === 'confirmed') {
          this.swalService.success(
            'Rendez-vous confirmé',
            'Votre rendez-vous a été confirmé avec succès',
          );
        } else {
          this.swalService.success(
            'Rendez-vous pris',
            'Votre demande de rdv a été envoyée avec succès',
          );
        }
        this.closeModal();
        this.router.navigate([
          `/${this.selectedCountry?.code}/passport/health-tracking`,
        ]);
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Erreur lors de la prise de rendez-vous:', error);
        this.swalService.error(
          'Erreur',
          'Une erreur est survenue lors de la réservation du rendez-vous',
        );
      },
    });
  }

  closeModal(): void {
    this.modalService.closeAll();
  }

  selectPatient(patientId: string): void {
    this.selectedPatientId = patientId;
    this.appointmentForm.patchValue({ patientId: patientId });
    console.log('Patient sélectionné:', patientId);
  }

  getPatientName(patient: any): string {
    if (patient?.firstName && patient?.lastName) {
      return `${patient.firstName} ${patient.lastName}`;
    }
    return patient?.passportNumber || 'Patient inconnu';
  }

  /** Patient sélectionné (pour le récap étape 2). */
  getSelectedPatient(): any {
    const sub = this.subAccounts.find(
      (a) => a.passportNumber === this.selectedPatientId,
    );
    if (sub) return sub;
    if (this.currentUser?.passportNumber === this.selectedPatientId)
      return this.currentUser;
    return null;
  }

  /**
   * Calcule le montant de l'accompte à payer
   */
  getDepositAmount(): number {
    if (this.slot?.acomptePrice !== undefined && this.slot.acomptePrice > 0) {
      return this.slot.acomptePrice;
    }
    return this.agenda?.paymentPolicy?.deposit?.amount || 0;
  }

  /**
   * Vérifie si un acompte est requis
   */
  requiresDeposit(): boolean {
    const hasAgendaDeposit =
      this.agenda?.paymentPolicy?.requiresDeposit === true;
    const hasSlotDeposit =
      this.slot?.acomptePrice !== undefined && this.slot.acomptePrice > 0;
    return Boolean(hasAgendaDeposit || hasSlotDeposit);
  }

  /**
   * Formate les minutes en texte lisible (heures/jours)
   */
  formatMinutes(minutes: number): string {
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      return days === 1 ? '1 jour' : `${days} jours`;
    } else if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      return hours === 1 ? '1 heure' : `${hours} heures`;
    }
    return `${minutes} minutes`;
  }

  /**
   * Formate l'affichage d'une règle d'annulation
   */
  formatCancellationRule(rule: {
    minMinutesBefore: number;
    refundPercent: number;
  }): string {
    // Cas spécial : aucune annulation possible (0 minutes, 0% remboursement)
    // minMinutesBefore === 0 signifie que le rendez-vous est déjà arrivé ou passé
    if (rule.minMinutesBefore === 0 && rule.refundPercent === 0) {
      return 'Annulation après le début du rendez-vous : aucun remboursement';
    }

    // Cas normal : affichage standard
    const timeBefore = this.formatMinutes(rule.minMinutesBefore);
    return `Annulation ${timeBefore} avant le rendez-vous : ${rule.refundPercent}% de remboursement`;
  }

  /**
   * Récupère les règles d'annulation
   */
  getCancellationRules(): Array<{
    minMinutesBefore: number;
    refundPercent: number;
  }> {
    return this.agenda?.paymentPolicy?.cancellation?.rules || [];
  }

  /**
   * Formate le montant pour l'affichage
   */
  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: this.agenda?.paymentPolicy?.deposit?.currency || 'XOF',
      minimumFractionDigits: 0,
    }).format(amount);
  }
}
